/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Toast from 'react-native-toast-message';
import { View, StyleSheet, ScrollView } from 'react-native';
import ProgressBar from '@components/global/ProgressBar';
import SociollaText from '@components/sociolla/SociollaText';
import TitleSubSection from '@components/global/TitleSubSection';
import Image from '@components/global/Image';
import ProductItem from '@components/global/ProductItem';
import SelectProductTable from '@components/form/SelectProductTable';
import Colors from '@global/colors';
import Button from '@components/form/Button';
import Input from '@components/form/Input';
import SelectBox from '@components/form/SelectBox';
import Table from '@components/table/Table';
import * as Layout from '@components/screen/Layout';
import {
  isWeb,
  isMobileWeb,
  isAndroid,
  isIos,
  fullHeight,
} from '@global/screens';
import { showPopup } from '@/store/modal';
import { showAlert } from '@/store/global';
import { convertProductMarketType } from '@global/helpers';
import { BOOLEAN_STATUS, FLAG_TYPE } from '@/constants';
import useApi from '@/hooks/useApi';
import { MsCatalog } from '@api/ms-catalog';

const tableStructure = {
  image: {
    head: 'Image',
    body: 'image',
    type: 'image',
  },
  product_name: {
    head: 'Product Name',
    body: 'product_name',
  },
  action: {
    head: '',
    type: 'action_icon',
    actions: [
      {
        name: 'delete',
      },
    ],
  },
};

const limit = 10;

const FormValidationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required.'),
  description: Yup.string().required('Description is required.'),
  is_active: Yup.boolean(),
  flag_type: Yup.object().required('Flag Type is required.'),
});

const isMobileDisabled = (values) => {
  return Boolean(values.name && values.description && values.flag_type);
};

const NewArrivalBestSellerFormScreen = (props) => {
  const dispatch = useDispatch();

  const [page, setPage] = useState(1);
  const [detailData, setDetailData] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [skip, setSkip] = useState(0);
  const [products, setProducts] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [showProduct, setShowProduct] = useState(false);

  const getNewArrivalBestSellerDetail = useApi(
    MsCatalog.getNewArrivalBestSellerDetail,
  );

  const getNewArrivalBestSellerProducts = async (id, newSkip = 0) => {
    const isMore = newSkip > 0;
    try {
      setSkip(newSkip);
      if (isMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      const response = await MsCatalog.getNewArrivalBestSellerProduct(id, {
        params: {
          skip: newSkip,
          limit: limit,
        },
      });
      if (response?.data?.data) {
        const result = response.data.data.map((item) => {
          item = convertProductMarketType(item);
          return item;
        });
        if (isMore) {
          const finalResult = products.concat(result);
          setProducts(finalResult);
        } else {
          setProducts(result);
        }
      } else {
        if (!isMore) {
          setProducts([]);
        }
      }
      if (isMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    } catch (err) {
      if (isMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };

  const getDetail = async (id) => {
    const response = await getNewArrivalBestSellerDetail.request(id);
    if (response?.data?.data && Object.keys(response.data.data).length) {
      const result = response.data.data;
      result.id = result._id;
      getNewArrivalBestSellerProducts(result._id);
      setDetailData(result);
    }
  };

  const goBack = () => {
    // On edit, return to the detail (not the list); on add, back to the list.
    if (isEdit) {
      props.navigation.navigate(
        'NewArrivalBestSellerDetailScreen',
        props.route.params,
      );
    } else {
      props.navigation.navigate('NewArrivalBestSellerScreen');
    }
  };

  const onSelectProduct = async (data) => {
    if (!isEdit) {
      const newProduct = JSON.parse(JSON.stringify(products));
      newProduct.push(convertProductMarketType(data));
      setProducts(newProduct);
      setShowProduct(false);
      Toast.show({
        text1: 'Successfully added product',
        type: 'success',
      });
    } else {
      try {
        dispatch(showPopup({ name: 'isLoading', value: true }));
        const response = await MsCatalog.createNewArrivalBestSellerProduct(
          detailData.id,
          {
            product_id: data.id,
            combination_id: data.product_combination_id,
            price: data.price,
          },
        );

        if (response?.data?.data?._id) {
          const newProduct = JSON.parse(JSON.stringify(products));
          newProduct.push(convertProductMarketType(data));
          setProducts(newProduct);
          setShowProduct(false);
          Toast.show({
            text1: 'Successfully added product',
            type: 'success',
          });
        } else {
          Toast.show({ text1: 'A system error occurred', type: 'error' });
        }
        dispatch(showPopup({ name: 'isLoading', value: false }));
      } catch (err) {
        console.log('err', err);
        dispatch(showPopup({ name: 'isLoading', value: false }));
        Toast.show({
          text1: err?.response?.data?.message || 'A system error occurred',
          type: 'error',
        });
      }
    }
  };

  const onRemoveProduct = (data) => {
    dispatch(
      showAlert({
        show: true,
        title: 'Remove Product',
        description: 'This product will be deleted. Do you want to proceed?',
        onCallback: async () => {
          try {
            dispatch(showPopup({ name: 'isLoading', value: true }));
            const response = await MsCatalog.deleteNewArrivalBestSellerProduct(
              detailData.id,
              data.product.id,
              data.product_combination_id,
            );
            if (response?.data?.data?._id) {
              const newProduct = JSON.parse(JSON.stringify(products));
              const idx = newProduct.findIndex(
                (item) =>
                  item.product.id == data.product.id &&
                  item.product_combination_id == data.product_combination_id,
              );
              if (idx > -1) {
                newProduct.splice(idx, 1);
                setProducts(newProduct);
                Toast.show({
                  text1: 'Successfully removed product',
                  type: 'success',
                });
              }
            } else {
              Toast.show({ text1: 'A system error occurred', type: 'error' });
            }
            dispatch(showPopup({ name: 'isLoading', value: false }));
          } catch (err) {
            dispatch(showPopup({ name: 'isLoading', value: false }));
            Toast.show({
              text1: err?.response?.data?.message || 'A system error occurred',
              type: 'error',
            });
          }
        },
      }),
    );
  };

  const removeInitProduct = (data) => {
    const selectedId = data.id;
    dispatch(
      showAlert({
        show: true,
        title: 'Remove Product',
        description: 'This product will be deleted. Do you want to proceed?',
        onCallback: () => {
          const deleted = products.findIndex(
            (product) => product.id === selectedId,
          );
          if (deleted !== -1) {
            const updatedProducts = [
              ...products.slice(0, deleted),
              ...products.slice(deleted + 1),
            ];
            setProducts(updatedProducts);
          }
        },
      }),
    );
  };

  const onMapAction = ({ data, index, action }) => {
    if (data) {
      if (action.name == 'delete') {
        if (isEdit) {
          onRemoveProduct(data);
        } else {
          removeInitProduct(data);
        }
      }
    }
  };

  const flagType = (val) => {
    return val?.value ? val?.value : val;
  };

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      setSkip(0);
      setIsEdit(false);
      setShowProduct(false);
      setDetailData({});
      if (props?.route?.params?.id) {
        setIsEdit(true);
        getDetail(props.route.params.id);
      }
      if (!isEdit) {
        setProducts([]);
      }
    }
    return () => {
      mounted = false;
    };
  }, [props]);

  return (
    <Layout.Container>
      {!isWeb && <ProgressBar page={page} totalPage={2} />}
      {isEdit && getNewArrivalBestSellerDetail?.loading ? (
        <Layout.Wrapper>
          <Layout.Loader />
        </Layout.Wrapper>
      ) : (isEdit ? detailData && Object.keys(detailData).length : true) ? (
        <Formik
          initialValues={{
            name: isEdit && detailData?.name ? detailData.name : '',
            description:
              isEdit && detailData?.description ? detailData.description : '',
            is_active:
              isEdit && detailData?.is_active ? detailData.is_active : false,
            flag_type:
              isEdit &&
              detailData?.flag_type &&
              FLAG_TYPE.find((a) => a.value == detailData.flag_type)
                ? FLAG_TYPE.find((a) => a.value == detailData.flag_type)
                : '',
          }}
          validationSchema={FormValidationSchema}
          onSubmit={async (payload) => {
            dispatch(showPopup({ name: 'isLoading', value: true }));
            try {
              const finalPayload = {
                name: payload.name,
                description: payload.description,
                is_active: payload.is_active,
                flag_type: payload.flag_type?.value,
              };

              if (!isEdit) {
                const modifiedProducts = [];
                products.forEach((product) => {
                  const modifiedProduct = {
                    product_id: product.id,
                    combination_id: product.product_combination_id,
                    price: product.price,
                  };

                  modifiedProducts.push(modifiedProduct);
                });
                const addnewPayload = {
                  products: modifiedProducts,
                  ...finalPayload,
                };
                const createData = await MsCatalog.createNewArrivalBestSeller(
                  addnewPayload,
                );
                if (createData?.data?.data) {
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  Toast.show({
                    text1: 'Successfully added data',
                    type: 'success',
                  });
                  props.navigation.navigate(
                    'NewArrivalBestSellerDetailScreen',
                    {
                      id: createData?.data?.data?._id,
                      name: createData?.data?.data?.name,
                      titleScreen: createData?.data?.data?.name,
                    },
                  );
                } else {
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  Toast.show({
                    text1: 'A system error occurred',
                    type: 'error',
                  });
                }
              } else {
                const updateData = await MsCatalog.updateNewArrivalBestSeller(
                  detailData._id,
                  finalPayload,
                );
                if (updateData?.data?.data) {
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  Toast.show({
                    text1: 'Successfully edited data',
                    type: 'success',
                  });
                  props.navigation.navigate(
                    'NewArrivalBestSellerDetailScreen',
                    {
                      id: updateData?.data?.data?._id,
                      name: updateData?.data?.data?.name,
                      titleScreen: updateData?.data?.data?.name,
                    },
                  );
                } else {
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  Toast.show({
                    text1: 'A system error occurred',
                    type: 'error',
                  });
                }
              }
              dispatch(showPopup({ name: 'isLoading', value: false }));
            } catch (error) {
              console.log(error);
              dispatch(showPopup({ name: 'isLoading', value: false }));
              Toast.show({
                text1:
                  error?.response?.data?.message || 'A system error occurred',
                type: 'error',
              });
            }
          }}>
          {({
            setFieldValue,
            validateForm,
            handleSubmit,
            errors,
            touched,
            values,
          }) => (
            <>
              {isWeb ? (
                <Layout.Wrapper white={false}>
                  <Layout.Back
                    title="Best Seller / New Arrival"
                    onCallback={() => goBack()}
                  />
                  <Layout.Title
                    title={`${
                      isEdit ? `Edit` : `Add`
                    } Best Seller / New Arrival`}>
                    <View style={styles.row}>
                      <Button
                        type="primary"
                        rounded
                        buttonStyle={{ width: 120 }}
                        title={
                          <SociollaText
                            type="bold"
                            style={{
                              fontSize: 14,
                              letterSpacing: 0.5,
                              color: Colors.white,
                            }}>
                            {isEdit ? 'SAVE' : 'ADD'}
                          </SociollaText>
                        }
                        onPress={handleSubmit}
                      />
                    </View>
                  </Layout.Title>
                </Layout.Wrapper>
              ) : (
                false
              )}
              <Layout.Wrapper rounded hasProgressBar={!isWeb}>
                <ScrollView
                  style={isMobileWeb ? { height: fullHeight - 55 } : {}}>
                  {page == 1 || isWeb ? (
                    <Layout.FormWrapper
                      style={{
                        paddingHorizontal: 15,
                        paddingTop: 20,
                        ...(isWeb
                          ? {
                              flexDirection: 'row',
                              justifyContent: 'space-between',
                              paddingHorizontal: 20,
                              marginBottom: 15,
                            }
                          : {
                              paddingBottom: !isMobileWeb
                                ? isAndroid
                                  ? 150
                                  : 80
                                : 100,
                            }),
                      }}>
                      <View style={styles.rowForm}>
                        <Layout.FormGroup>
                          <Input
                            label="Name"
                            value={values.name}
                            isRequired
                            placeholder="Name"
                            hasError={
                              errors.name && touched.name ? errors.name : null
                            }
                            onChangeText={(val) => {
                              setFieldValue('name', val);
                            }}
                          />
                        </Layout.FormGroup>
                        <Layout.FormGroup>
                          <Input
                            label="Description"
                            value={values.description}
                            isRequired
                            placeholder="Description"
                            hasError={
                              errors.description && touched.description
                                ? errors.description
                                : null
                            }
                            onChangeText={(val) => {
                              setFieldValue('description', val);
                            }}
                          />
                        </Layout.FormGroup>
                        <Layout.FormGroup style={isWeb ? { zIndex: 1000 } : {}}>
                          <SelectBox
                            label="Status"
                            isDropdown={isWeb}
                            options={BOOLEAN_STATUS}
                            value={
                              values.is_active
                                ? BOOLEAN_STATUS[0]
                                : BOOLEAN_STATUS[1]
                            }
                            placeholder="Select Status"
                            trackBy="value"
                            labelKey="name"
                            isRequired
                            hasError={
                              errors.is_active && touched.is_active
                                ? errors.is_active
                                : null
                            }
                            onChange={(value) =>
                              setFieldValue('is_active', value.value)
                            }
                          />
                        </Layout.FormGroup>
                        <Layout.FormGroup>
                          <SelectBox
                            label="Flag Type"
                            options={FLAG_TYPE}
                            value={values.flag_type}
                            placeholder="Select Flag Type"
                            trackBy="value"
                            labelKey="name"
                            isRequired
                            hasError={
                              errors.flag_type && touched.flag_type
                                ? errors.flag_type
                                : null
                            }
                            onChange={(value) => {
                              setFieldValue('flag_type', value);
                            }}
                          />
                        </Layout.FormGroup>
                      </View>
                    </Layout.FormWrapper>
                  ) : (
                    false
                  )}
                  {page == 2 || isWeb ? (
                    <>
                      {isWeb ? (
                        <TitleSubSection
                          title="PRODUCT"
                          disabled={loading}
                          button={
                            (
                              flagType(values?.flag_type) == 'best_seller'
                                ? products?.length < 1000
                                : true
                            )
                              ? {
                                  label: 'Add Product',
                                  callback: () => setShowProduct(true),
                                }
                              : false
                          }
                        />
                      ) : (
                        false
                      )}
                      <Layout.FormWrapper
                        style={{
                          paddingTop: 10,
                          paddingHorizontal: isWeb ? 20 : 15,
                          ...(!products?.length
                            ? { justifyContent: 'center' }
                            : {}),
                          paddingBottom: isAndroid
                            ? 170
                            : isIos
                            ? 120
                            : isMobileWeb
                            ? 140
                            : 10,
                        }}>
                        {loading ? (
                          <Layout.Loader />
                        ) : !products?.length ? (
                          <View
                            style={{
                              paddingVertical: 20,
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}>
                            <Image
                              style={{ width: 30, height: 35 }}
                              width={30}
                              height={35}
                              source={require('@assets/img/icons/no-product.svg')}
                              svg
                            />
                            <SociollaText style={{ marginVertical: 10 }}>
                              No product found
                            </SociollaText>
                            {!isWeb ? (
                              <Button
                                type="default"
                                rounded
                                buttonStyle={{ width: 150, marginBottom: 10 }}
                                disabledStyle={{ width: 150, marginBottom: 10 }}
                                title={
                                  <SociollaText
                                    type="bold"
                                    style={{
                                      fontSize: 14,
                                      letterSpacing: 0.5,
                                      color: Colors.black,
                                    }}>
                                    Add Product
                                  </SociollaText>
                                }
                                onPress={() => setShowProduct(true)}
                              />
                            ) : (
                              false
                            )}
                          </View>
                        ) : isWeb ? (
                          <Table
                            structures={tableStructure}
                            data={products}
                            withFetch={false}
                            hasNoHide
                            showFilter={false}
                            noTopSection
                            showPagination={false}
                            withoutShadow
                            totalData={products.length}
                            onMapAction={(val) => onMapAction(val)}
                          />
                        ) : (
                          products.map((item, idx) => (
                            <ProductItem
                              data={item}
                              onRemove={() => onRemoveProduct(item)}
                              showRemove
                              isDetail
                              withoutPrice
                              key={idx}
                            />
                          ))
                        )}
                        {loadingMore ? (
                          <View style={[styles.row, styles.buttonWrapper]}>
                            <Layout.Loader />
                          </View>
                        ) : !loadingMore &&
                          products?.length >= limit &&
                          skip <= products?.length ? (
                          <View style={[styles.row, styles.buttonWrapper]}>
                            <Button
                              rounded
                              type="primary"
                              buttonStyle={{ width: 150 }}
                              title={
                                <SociollaText
                                  type="bold"
                                  style={{
                                    fontSize: 13,
                                    letterSpacing: 0.5,
                                    color: Colors.white,
                                  }}>
                                  Load More
                                </SociollaText>
                              }
                              onPress={() =>
                                getNewArrivalBestSellerProducts(
                                  detailData?._id,
                                  skip + limit,
                                )
                              }
                            />
                          </View>
                        ) : (
                          false
                        )}
                      </Layout.FormWrapper>
                    </>
                  ) : (
                    false
                  )}
                </ScrollView>
              </Layout.Wrapper>
              {!isWeb ? (
                <Layout.ButtonSticky
                  show={
                    props?.route?.name == 'NewArrivalBestSellerFormScreen' &&
                    [1, 2].includes(page)
                  }
                  buttons={
                    page == 1
                      ? [
                          {
                            title: isEdit ? 'Next' : 'Save',
                            disabled: !isMobileDisabled(values),
                            onPress: (evt) =>
                              isEdit ? setPage(2) : handleSubmit(evt),
                          },
                        ]
                      : page == 2
                      ? [
                          ...(products &&
                          products.length &&
                          (flagType(values?.flag_type) == 'best_seller'
                            ? products?.length < 1000
                            : true) &&
                          isMobileDisabled(values)
                            ? [
                                {
                                  title: 'Add Product',
                                  type: 'default',
                                  onPress: () => setShowProduct(true),
                                },
                              ]
                            : []),
                          {
                            title: 'Save',
                            onPress: (evt) => {
                              evt.persist();
                              setTimeout(() => {
                                validateForm().then((validate) => {
                                  handleSubmit(evt);
                                  if (
                                    validate &&
                                    Object.keys(validate).length
                                  ) {
                                    setPage(1);
                                  }
                                });
                              }, 100);
                            },
                          },
                        ]
                      : []
                  }
                />
              ) : (
                // <View style={buttonFloating}>
                //   <Button
                //     rounded
                //     buttonStyle={{ width: '100%' }}
                //     title={
                //       <SociollaText
                //         type="bold"
                //         style={{
                //           fontSize: 14,
                //           letterSpacing: 0.5,
                //           color: Colors.white,
                //         }}>
                //         SAVE
                //       </SociollaText>
                //     }
                //     onPress={handleSubmit}
                //   />
                // </View>
                false
              )}
            </>
          )}
        </Formik>
      ) : (
        false
      )}
      <SelectProductTable
        show={showProduct}
        onClose={() => setShowProduct(false)}
        onCallback={(val) => onSelectProduct(val)}
      />
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  row: { flexDirection: 'row' },
  buttonWrapper: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 15,
  },
  rowForm: { width: isWeb ? 'calc(50% - 20px)' : '100%' },
});

export default NewArrivalBestSellerFormScreen;
